import { useLocation } from "react-router-dom";
import { handleUIEvent } from "./eventLogger.js";
import './RainFall.css';
import CalendarSelector from "./_42_calendar_selector.js";
import { useDateFilter } from "./_40b_date_filter_context.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";
import { cachedGet } from "./apiCache.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";
import { useState, useEffect, useRef } from "react";

const MODULE_NAME = "_1_7_day_week_PL_tiles_v3";

function useIsMobile(breakpoint = 760) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);
    return isMobile;
}

function formatCurrency(val) {
    const n = Number(val);
    if (isNaN(n)) return "";
    const color = n > 0 ? "green" : n < 0 ? "red" : "gray";
    return (
        <span style={{ color, fontWeight: 600 }}>
            {n < 0 ? "-$" : "$"}{Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
    );
}

function formatActiveDateTitle(dateStr) {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[Number(parts[1]) - 1];
    const day = parts[2];
    const year = parts[0];
    return `${month} ${day}, ${year}`;
}

function getMonday(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayOfWeek = "Mon";
    const month = monthNames[monday.getMonth()];
    const dayNum = monday.getDate();

    return `${dayOfWeek} ${month} ${dayNum}`;
}

export default function SevenDayPLTiles({ activeDate, onDateChange, screen, refreshKey }) {
    const location = useLocation();
    const { navigationDate, setNavigationDate } = useDateFilter();

    const queryParams = new URLSearchParams(location.search);
    let dateFromQuery = queryParams.get("date");
    if (!dateFromQuery || !/^\d{4}-\d{2}-\d{2}$/.test(dateFromQuery)) {
        dateFromQuery = null;
    }

    const initialDate =
        (navigationDate && /^\d{4}-\d{2}-\d{2}$/.test(navigationDate) ? navigationDate : null) ||
        (activeDate && /^\d{4}-\d{2}-\d{2}$/.test(activeDate) ? activeDate : null) ||
        (dateFromQuery ? dateFromQuery : null) ||
        (sessionStorage.getItem("navigationDate") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("navigationDate")) ? sessionStorage.getItem("navigationDate") : null) ||
        (sessionStorage.getItem("latest_date") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("latest_date")) ? sessionStorage.getItem("latest_date") : null) ||
        new Date().toISOString().slice(0, 10);

    // DECLARE ALL HOOKS HERE - BEFORE ANY CONDITIONAL LOGIC
    const [summary, setSummary] = useState([]);
    const [tooltip, setTooltip] = useState("");
    const [selectedDate, setSelectedDate] = useState(initialDate);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [fullSummary, setFullSummary] = useState([]);
    const [timeframe, setTimeframe] = useState("daily");
    const [badgeIsOpen, setBadgeIsOpen] = useState(() => {
        const navDateFromStorage = sessionStorage.getItem("navigationDate");
        const latestDate = sessionStorage.getItem("latest_date");
        return !!navDateFromStorage && navDateFromStorage !== latestDate;
    });

    const scrollRef = useRef(null);
    const isMobile = useIsMobile();
    const visibleCards = isMobile ? summary.slice(-2) : summary;

    // DECLARE ALL useEffect HOOKS HERE - BEFORE ANY CONDITIONAL LOGIC
    useEffect(() => {
        const handleBadgeStateChange = (e) => {
            const latestDate = sessionStorage.getItem("latest_date");
            const navDateFromStorage = sessionStorage.getItem("navigationDate");
            const isBadgeNowClosed = !navDateFromStorage || navDateFromStorage === latestDate;
            setBadgeIsOpen(!isBadgeNowClosed);
        };

        window.addEventListener("navigationDateChanged", handleBadgeStateChange);
        return () => window.removeEventListener("navigationDateChanged", handleBadgeStateChange);
    }, []);

    useEffect(() => {
        setLoading(true);
        setError("");

        const anchorDate = navigationDate || selectedDate || new Date().toISOString().slice(0, 10);
        const [anchorYear, anchorMonth] = anchorDate.split("-").map(Number);

        const currentMode = timeframe === "daily" ? "5day" : "days";

        const getQueryParams = {
            year: anchorYear,
            month: anchorMonth,
            mode: currentMode,
            instrument_type: "STOCK"
        };

        cachedGet(`${process.env.REACT_APP_API_URL}/api/html-calendar-month`, getQueryParams, { withCredentials: true })
            .then(res => {
                const data = parseBackendSummary(res.data, currentMode);

                setTooltip(res.data?.tooltip || "");
                setFullSummary(data);

                const seen = new Set();
                const filteredData = data.filter(day => {
                    if (seen.has(day.fulldate)) return false;
                    seen.add(day.fulldate);
                    return true;
                });

                const displayCards = currentMode === "5day" ? filteredData : filteredData.slice(-5);
                setSummary(displayCards);
                setLoading(false);

                if (filteredData.length > 0) {
                    const matchedDate = filteredData.find(d => d.fulldate === anchorDate);
                    if (matchedDate) {
                        setSelectedDate(matchedDate.fulldate);
                    } else {
                        const dayToSelect = displayCards.length > 0 ? displayCards[displayCards.length - 1] : null;
                        if (dayToSelect) setSelectedDate(dayToSelect.fulldate);
                    }
                }
            })
            .catch(err => {
                setLoading(false);
                setError("Failed to load summary.");
            });
    }, [timeframe, refreshKey, navigationDate]);

    useEffect(() => {
        if (navigationDate && navigationDate !== selectedDate) {
            setSelectedDate(navigationDate);
        }
    }, [navigationDate]);

    useEffect(() => {
        const latestDate = sessionStorage.getItem("latest_date");

        if (!badgeIsOpen && timeframe === "daily" && summary.length > 0) {
            const latestDay = summary[summary.length - 1];
            if (latestDay && selectedDate !== latestDay.fulldate) {
                setSelectedDate(latestDay.fulldate);
            }
        } else if (!badgeIsOpen && timeframe !== "daily") {
            if (latestDate && summary.some(d => d.fulldate === latestDate)) {
                setSelectedDate(latestDate);
            }
        }
     }, [badgeIsOpen, timeframe, summary]);

    // Check if in fullscreen mode
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    // Set fullscreen indicator on mount/unmount
    useEffect(() => {
        if (isFullscreen) {
            document.documentElement.setAttribute('data-fullscreen', 'true');
            return () => document.documentElement.removeAttribute('data-fullscreen');
        }
    }, [isFullscreen]);

    // NOW CHECK VISIBILITY - AFTER ALL HOOKS ARE DECLARED
    const isVisible = useModuleVisibility(MODULE_NAME);

    if (!isVisible) return null;

    function Rainfall() {
        const drops = Array.from({ length: 18 }).map((_, i) => {
            const left = Math.random() * 96 + 2;
            const delay = Math.random() * .2;
            return <div key={i} className="raindropOFF" style={{ left: `${left}%`, animationDelay: `${delay}s` }} />;
        });
        return <div className="rainfall-container">{drops}</div>;
    }

    const parseBackendSummary = (resData, mode) => {
        if (mode === "5day") {
            return (resData.summary || []).map(day => ({
                ...day,
                total_profit: day.profit
            }));
        }

        const summaries = [];
        if (!resData.grid) return summaries;

        resData.grid.forEach(week => {
            const firstCell = week.find(c => c !== null);
            if (timeframe === "weekly" && firstCell?.weekTotal !== undefined) {
                const cellWithDate = week.find(c => c?.fulldate);
                if (cellWithDate) {
                    summaries.push({
                        date: getMonday(cellWithDate.fulldate),
                        fulldate: cellWithDate.fulldate,
                        symbols_traded: firstCell.week_symbols_traded || 0,
                        num_trades: week.reduce((sum, c) => sum + (c?.num_trades || 0), 0),
                        total_profit: firstCell.weekTotal || 0.0
                    });
                }
            } else if (timeframe === "daily") {
                week.forEach(cell => {
                    if (cell?.profit !== undefined) {
                        summaries.push({
                            date: cell.date || "",
                            fulldate: cell.fulldate || "",
                            symbols_traded: cell.symbols_traded || 0,
                            num_trades: cell.num_trades || 0,
                            total_profit: cell.profit || 0.0
                        });
                    }
                });
            }
        });
        return summaries;
    };

    const handleCardClick = (date, idx, e) => {
        if (timeframe !== "daily") return;

        const dayObj = visibleCards[idx];
        const navDateFormatted = dayObj.fulldate && /^\d{4}-\d{2}-\d{2}$/.test(dayObj.fulldate)
            ? dayObj.fulldate
            : date;

        setSelectedDate(navDateFormatted);
        sessionStorage.setItem("navigationDate", navDateFormatted);
        setNavigationDate(navDateFormatted);
        setBadgeIsOpen(true);

        const event = new CustomEvent("navigationDateChanged", { detail: { date: navDateFormatted } });
        window.dispatchEvent(event);

        if (onDateChange) onDateChange(navDateFormatted);
        handleUIEvent(e, { action: "card_click", date: navDateFormatted }, null, screen, MODULE_NAME);
    };

    const largestLossIdx = summary.length
        ? summary.reduce((minIdx, day, idx, arr) => day.total_profit < arr[minIdx].total_profit ? idx : minIdx, 0)
        : -1;

    if (loading) return <div>Loading summary...</div>;
    if (error) return <div style={{ color: "red", margin: "16px 0" }}>{error}</div>;

    const cardBaseStyle = {
        borderRadius: 6, padding: 8, textAlign: "center", position: "relative",
        boxShadow: "0 1px 3px rgba(0,0,0,0.18)", cursor: "pointer", transition: "background 0.2s, border 0.2s",
        minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "center"
    };

    const timeframeOptions = [
        { key: "daily", label: "Daily" },
        { key: "weekly", label: "Weekly" }
    ];

    return (
        <div data-analytics={MODULE_NAME} style={getFullscreenContainerStyles(isFullscreen)}>
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <h4 style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 0, flex: 1 }}>
                    <span> 🗓️ Last 5 Trading Days <span style={{ marginLeft: 8, verticalAlign: "middle", cursor: "pointer", color: "#888", fontSize: 18 }} title={tooltip}>🛈</span></span>
                    <span style={{ display: "flex", gap: 12 }}>
                        {timeframeOptions.map(opt => (
                            <a key={opt.key} href="#" onClick={e => { e.preventDefault(); setTimeframe(opt.key); }} style={{ color: timeframe === opt.key ? "#4ECDC4" : "#888", fontWeight: timeframe === opt.key ? 700 : 500, textDecoration: "none", fontSize: 12, padding: "0 4px" }}>
                                {opt.label}
                            </a>
                        ))}
                    </span>
                </h4>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="7-Day/Week PL Tiles"
                        additionalParams={{ timeframe }}
                    />
                )}
            </div>
            <div style={{ marginTop: 0, display: "flex", alignItems: "stretch", gap: 12, width: "100%" }}>
                <div ref={scrollRef} style={{ display: "flex", flex: 1, gap: 16, overflowX: isMobile ? "auto" : "hidden", paddingBottom: isMobile ? 4 : 0 }}>
                    {visibleCards.map((day, idx) => {
                        const isSelected = day.fulldate === selectedDate;
                        const profitColor = day.total_profit >= 0 ? "green" : "red";

                        let hoverDate = "";
                        if (day.fulldate) {
                            const [y, m, d] = day.fulldate.split("-");
                            hoverDate = `${m}/${d}/${y.slice(-2)}`;
                        }

                        return (
                            <div
                                key={day.fulldate || idx}
                                onClick={e => handleCardClick(day.fulldate, idx, e)}
                                title={hoverDate}
                                style={{
                                    ...cardBaseStyle,
                                    flex: isMobile ? "0 0 120px" : "1 1 0",
                                    minWidth: isMobile ? 120 : 0,
                                    background: isSelected ? "#10121A" : "#181A25",
                                    border: isSelected ? "2px solid #4ECDC4" : "1px solid #3a3d46",
                                    boxShadow: isSelected ? "0 2px 8px rgba(78,205,196,0.35)" : "0 1px 4px rgba(0,0,0,0.25)",
                                    overflow: "hidden",
                                    animation: `slideInCard 0.6s cubic-bezier(0.23, 1, 0.32, 1) ${idx * 0.15}s both`
                                }}
                            >
                                <div style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    height: "3px",
                                    background: "linear-gradient(90deg, #4ECDC4, #50E3C2, transparent)",
                                    animation: `progressBar 1s ease-out ${idx * 0.15}s forwards`,
                                    transformOrigin: "left"
                                }} />
                                {idx === largestLossIdx && <Rainfall />}
                                <div style={{ position: "absolute", top: 6, right: 8, fontSize: 11, color: "#888" }}>{day.num_trades} trades</div>
                                <div className="seven_tiles_dateformat">
                                    {day.date}
                                </div>
                                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{day.symbols_traded} symbol(s)</div>
                                <div style={{ fontSize: 16, fontWeight: 600, color: profitColor }}> {formatCurrency(day.total_profit)} </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <style>
                {`
                    @keyframes slideInCard {
                        from {
                            opacity: 0;
                            transform: translateY(16px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes progressBar {
                        from {
                            width: 0%;
                        }
                        to {
                            width: 100%;
                        }
                    }
                `}
            </style>
        </div>
    );
}